import React, { useEffect, useMemo, useState, memo } from "react";

import * as XLSX from "xlsx";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
  Card,
  CircularProgress,
  TextField,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TableViewIcon from "@mui/icons-material/TableView";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

function toRef(x) {
  if (x === null || x === undefined) return "";
  if (typeof x === "string" || typeof x === "number") return x.toString();

  return (x?.ref ?? x?.Ref ?? x?.value ?? x?.valor ?? x?.id ?? "").toString();
}

function toNome(x) {
  return (
    x?.nome ??
    x?.Nome ??
    x?.designacao ??
    x?.Designacao ??
    x?.label ??
    x?.value ??
    x?.valor ??
    x?.ref ??
    ""
  ).toString();
}

function normalizeMulti(arr) {
  return Array.isArray(arr)
    ? arr
        .map((x) => ({
          ref: toRef(x).trim(),
          nome: toNome(x).trim(),
        }))
        .filter((x) => x.ref)
    : [];
}

function arraysIguaisSemOrdem(a = [], b = []) {
  if (a.length !== b.length) return false;

  const aa = [...a].map((x) => String(x)).sort();
  const bb = [...b].map((x) => String(x)).sort();

  return aa.every((valor, idx) => valor === bb[idx]);
}

const ChecklistGroup = memo(function ChecklistGroup({
  title,
  filterValue,
  onFilterChange,
  options,
  selectedRefs,
  onToggle,
  onToggleAll,
  emptyText = "Sem resultados para os filtros indicados.",
}) {
  const visibleRefs = useMemo(() => options.map((x) => x.ref), [options]);

  const selectedRefsSet = useMemo(() => new Set(selectedRefs), [selectedRefs]);

  const allSelected = useMemo(
    () => visibleRefs.length > 0 && visibleRefs.every((ref) => selectedRefsSet.has(ref)),
    [visibleRefs, selectedRefsSet]
  );

  return (
    <>
      <MDBox mb={1}>
        <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 0.5 }}>
          Filtrar {String(title || "").toLowerCase()}
        </MDTypography>

        <TextField
          value={filterValue}
          onChange={(e) => onFilterChange(e.target.value)}
          placeholder="Escreva para filtrar por nome ou ref..."
          size="small"
          fullWidth
        />
      </MDBox>

      <MDBox mb={0.5}>
        <FormControlLabel
          control={<Checkbox size="small" checked={allSelected} onChange={onToggleAll} />}
          label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>}
        />
      </MDBox>

      <MDBox
        sx={{
          maxHeight: 220,
          overflowY: "auto",
          pr: 1,
          pl: 0.5,
        }}
      >
        <MDBox display="flex" flexWrap="wrap" gap={1}>
          {options.map((opt) => (
            <MDBox key={opt.ref} sx={{ minWidth: 260 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedRefsSet.has(opt.ref)}
                    onChange={() => onToggle(opt.ref)}
                  />
                }
                label={
                  <span style={{ fontSize: 13 }}>
                    {opt.nome || opt.ref} <span style={{ color: "#9e9e9e" }}>({opt.ref})</span>
                  </span>
                }
              />
            </MDBox>
          ))}

          {options.length === 0 && (
            <MDTypography variant="caption" color="text">
              {emptyText}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </>
  );
});

function VendaColumnFilter({ column }) {
  const { filterValue, setFilter } = column;

  return (
    <select
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "2px 4px",
        fontSize: 11,
        border: "none",
        borderBottom: "1px solid #e0e0e0",
        outline: "none",
        backgroundColor: "transparent",
      }}
    >
      <option value="">Todos</option>
      <option value="1">Com vendas</option>
      <option value="0">Sem vendas</option>
    </select>
  );
}

const ResultadosMatriz = memo(function ResultadosMatriz({ resultado, mostrarColunaVendedor }) {
  const [linhasVisiveis, setLinhasVisiveis] = useState([]);

  const columns = useMemo(() => {
    const cols = [
      {
        Header: "Cliente",
        accessor: "cliente",

        Cell: ({ row }) => {
          const nome = row.original?.cliente || "";
          const nif = row.original?.nif || "";
          return nif ? `${nome} (${nif})` : nome;
        },
      },
    ];

    if (mostrarColunaVendedor) {
      cols.push({
        Header: "Vendedor",
        accessor: "vendedor",
      });
    }

    cols.push({
      Header: "Total",
      id: "totalchecks",
      accessor: (row) =>
        (resultado?.submarcas || []).reduce((total, sm) => {
          const ref = (sm?.ref || "").toString().trim();
          return total + (Number(row?.[`sm_${ref}`] || 0) === 1 ? 1 : 0);
        }, 0),
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      Cell: ({ value }) => Number(value || 0),
    });

    (resultado?.submarcas || []).forEach((sm) => {
      const ref = (sm?.ref || "").toString().trim();
      const nome = (sm?.nome || ref || "").toString();

      if (!ref) return;

      cols.push({
        Header: nome,
        accessor: `sm_${ref}`,
        Filter: VendaColumnFilter,
        filter: "vendaEquals",
        width: 110,
        minWidth: 110,
        maxWidth: 110,
        Cell: ({ value }) =>
          Number(value || 0) === 1 ? <span style={{ fontSize: 16, lineHeight: 1 }}>✔</span> : "",
      });
    });

    return cols;
  }, [resultado, mostrarColunaVendedor]);

  const filterTypes = useMemo(
    () => ({
      vendaEquals: (rows, id, filterValue) => {
        if (filterValue === undefined || filterValue === null || filterValue === "") {
          return rows;
        }

        return rows.filter((row) => {
          const valor = Number(row.values[id] || 0);
          return String(valor) === String(filterValue);
        });
      },
    }),
    []
  );

  const exportarXlsx = () => {
    const dadosExportar = linhasVisiveis.map((linhaOriginal) => {
      const linha = {
        Cliente: linhaOriginal.nif
          ? `${linhaOriginal.cliente || ""} (${linhaOriginal.nif})`
          : linhaOriginal.cliente || "",
      };

      if (mostrarColunaVendedor) {
        linha.Vendedor = linhaOriginal.vendedor || "";
      }

      linha.Total = (resultado?.submarcas || []).reduce((total, sm) => {
        const ref = (sm?.ref || "").toString().trim();
        return total + (Number(linhaOriginal?.[`sm_${ref}`] || 0) === 1 ? 1 : 0);
      }, 0);

      (resultado?.submarcas || []).forEach((sm) => {
        const ref = (sm?.ref || "").toString().trim();
        const nome = (sm?.nome || ref || "").toString();

        if (ref) {
          linha[nome] = Number(linhaOriginal?.[`sm_${ref}`] || 0) === 1 ? "Sim" : "Não";
        }
      });

      return linha;
    });

    const ws = XLSX.utils.json_to_sheet(dadosExportar);

    // Ajuste de larguras de colunas
    const cols = [];

    // Cliente (mais largo)
    cols.push({ wch: 50 });

    // Vendedor (também largo, mas menos)
    if (mostrarColunaVendedor) {
      cols.push({ wch: 25 });
    }

    // Total (curto)
    cols.push({ wch: 8 });

    // Submarcas (todas iguais)
    (resultado?.submarcas || []).forEach(() => {
      cols.push({ wch: 12 });
    });

    ws["!cols"] = cols;

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Resultados");

    XLSX.writeFile(wb, "Propagacao_Submarcas.xlsx");
  };

  return (
    <MDBox mt={2}>
      <MDBox display="flex" alignItems="center" mb={1}>
        <MDTypography variant="h6">Resultados</MDTypography>

        <MDBox ml={1}>
          <Tooltip title="Exportar para Excel">
            <span>
              <IconButton
                onClick={exportarXlsx}
                disabled={linhasVisiveis.length === 0}
                color="info"
              >
                <TableViewIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
        </MDBox>
      </MDBox>

      <MDBox
        sx={{
          border: "1px solid #eee",
          borderRadius: 2,
          p: 2,
          backgroundColor: "#fafafa",
          mb: 2,
        }}
      >
        <MDTypography sx={{ fontSize: 13, color: "#666" }}>
          Submarcas selecionadas: {resultado.submarcas?.length || 0}
        </MDTypography>

        <MDTypography sx={{ fontSize: 13, color: "#666" }}>
          Clientes carregados: {resultado.linhas?.length || 0}
        </MDTypography>

        <MDTypography sx={{ fontSize: 13, color: "#666" }}>
          Clientes visíveis: {linhasVisiveis.length}
        </MDTypography>
      </MDBox>

      <ProjectTable
        tableKey="propagacao-submarcas-resultados"
        version="v1"
        columns={columns}
        data={Array.isArray(resultado?.linhas) ? resultado.linhas : []}
        loading={false}
        emptyMessage="Sem clientes para apresentar."
        minWidth={Math.max(
          900,
          340 + (mostrarColunaVendedor ? 220 : 0) + 80 + (resultado?.submarcas?.length || 0) * 110
        )}
        maxHeight="50vh"
        filterTypes={filterTypes}
        onVisibleRowsChange={setLinhasVisiveis}
        showKeepFilters
      />
    </MDBox>
  );
});

export default function PropagacaoSubmarcas() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const isGestaoSuperBock = temFuncao("Gestão Super Bock");
  const isGestaoCocaCola = temFuncao("Gestão Coca-Cola");
  const isGestaoFabricante = isGestaoSuperBock || isGestaoCocaCola;

  const podeGerirPropagacao = isAdminLike || isGestaoFabricante;

  const FABRICANTE_STORAGE_KEY = `propagacaoSubmarcas:fabricante:${userData?.id || 0}`;
  const DATAINI_STORAGE_KEY = `propagacaoSubmarcas:dataIni:${userData?.id || 0}`;
  const DATAFIM_STORAGE_KEY = `propagacaoSubmarcas:dataFim:${userData?.id || 0}`;

  const [loading, setLoading] = useState(false);

  const [dataIni, setDataIni] = useState("");
  const [dataFim, setDataFim] = useState("");

  const [vendedores, setVendedores] = useState([]);
  const [vendedorSel, setVendedorSel] = useState(null);

  const modosClientesOptions = [
    { value: "rota", label: "Clientes da rota" },
    { value: "todos", label: "Todos os clientes" },
  ];

  const [modoClientes, setModoClientes] = useState(modosClientesOptions[0]);

  const [fabricantes, setFabricantes] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [submarcas, setSubmarcas] = useState([]);

  const [fabricantesSel, setFabricantesSel] = useState([]);
  const [marcasSel, setMarcasSel] = useState([]);
  const [submarcasSel, setSubmarcasSel] = useState([]);

  const fabricanteSelecionado = fabricantesSel[0] || "";

  const podeEditarFabricanteSelecionado =
    isAdminLike ||
    (isGestaoSuperBock && fabricanteSelecionado === "1123110000") ||
    (isGestaoCocaCola && fabricanteSelecionado === "1123110039");

  const podeVerTodosNoFabricanteSelecionado =
    isAdminLike ||
    (isGestaoSuperBock && fabricanteSelecionado === "1123110000") ||
    (isGestaoCocaCola && fabricanteSelecionado === "1123110039");

  const [fabricantesGuardado, setFabricantesGuardado] = useState([]);
  const [marcasGuardado, setMarcasGuardado] = useState([]);
  const [submarcasGuardado, setSubmarcasGuardado] = useState([]);

  const [fabricantesFiltro, setFabricantesFiltro] = useState("");
  const [marcasFiltro, setMarcasFiltro] = useState("");
  const [submarcasFiltro, setSubmarcasFiltro] = useState("");

  const [expandedPanel, setExpandedPanel] = useState("fabricantes");

  const [resultado, setResultado] = useState({
    submarcas: [],
    linhas: [],
  });
  const [analiseCarregada, setAnaliseCarregada] = useState(false);

  const fabricantesAlterados = useMemo(() => {
    return !arraysIguaisSemOrdem(fabricantesSel, fabricantesGuardado);
  }, [fabricantesSel, fabricantesGuardado]);

  const marcasAlteradas = useMemo(() => {
    return !arraysIguaisSemOrdem(marcasSel, marcasGuardado);
  }, [marcasSel, marcasGuardado]);

  const submarcasAlteradas = useMemo(() => {
    return !arraysIguaisSemOrdem(submarcasSel, submarcasGuardado);
  }, [submarcasSel, submarcasGuardado]);

  const funcoesUser = useMemo(() => userData?.funcoes || [], [userData]);
  const idUtilizador = Number(userData?.id || 0);

  const fabricantesOpt = useMemo(() => {
    const term = (fabricantesFiltro || "").trim().toLowerCase();

    if (!term) {
      console.log("=== Filtro fabricantes ===");
      console.log("Sem filtro");
      console.log("Fabricantes:", fabricantes.length);
      return fabricantes;
    }

    const lista = fabricantes.filter((x) => {
      const nome = (x?.nome || "").toString().toLowerCase();
      const ref = (x?.ref || "").toString().toLowerCase();
      return nome.includes(term) || ref.includes(term);
    });

    console.log("=== Filtro fabricantes ===");
    console.log("Filtro:", term);
    console.log("Fabricantes antes:", fabricantes.length);
    console.log("Fabricantes depois:", lista.length);

    return lista;
  }, [fabricantes, fabricantesFiltro]);

  const marcasOpt = useMemo(() => {
    const term = (marcasFiltro || "").trim().toLowerCase();

    if (!term) return marcas;

    return marcas.filter((x) => {
      const nome = (x?.nome || "").toString().toLowerCase();
      const ref = (x?.ref || "").toString().toLowerCase();
      return nome.includes(term) || ref.includes(term);
    });
  }, [marcas, marcasFiltro]);

  const submarcasOpt = useMemo(() => {
    const term = (submarcasFiltro || "").trim().toLowerCase();

    if (!term) return submarcas;

    return submarcas.filter((x) => {
      const nome = (x?.nome || "").toString().toLowerCase();
      const ref = (x?.ref || "").toString().toLowerCase();
      return nome.includes(term) || ref.includes(term);
    });
  }, [submarcas, submarcasFiltro]);

  const mostrarColunaVendedor = useMemo(() => {
    const refs = new Set(
      (resultado?.linhas || []).map((x) => (x?.refvendedor || "").toString().trim()).filter(Boolean)
    );

    return (
      refs.size > 1 || ((vendedorSel?.ref || "") === "" && podeVerTodosNoFabricanteSelecionado)
    );
  }, [resultado, vendedorSel, podeVerTodosNoFabricanteSelecionado]);

  const onAccordionChange = (panel) => async (event, isExpanded) => {
    if (isExpanded) {
      if (expandedPanel === "marcas" && panel !== "marcas" && marcasAlteradas) {
        if (marcasSel.length === 0) {
          setSubmarcas([]);
          setSubmarcasSel([]);
          setSubmarcasGuardado([]);
          setSubmarcasFiltro("");
          setMarcasGuardado(marcasSel);
        } else {
          setLoading(true);

          try {
            const dataini = dataIni;
            const datafim = dataFim;
            const refvendedor = podeVerTodosNoFabricanteSelecionado ? vendedorSel?.ref || "" : "";

            const submarcasAtuais = [...submarcasSel];

            await carregarSubmarcas({
              dataini,
              datafim,
              refvendedor,
              fabricantes: fabricantesSel,
              marcas: marcasSel,
              submarcasGuardadas: submarcasAtuais,
            });

            setMarcasGuardado(marcasSel);
          } finally {
            setLoading(false);
          }
        }
      }

      setExpandedPanel(panel);
      return;
    }

    if (panel === "fabricantes") {
      if (fabricantesSel.length === 0) {
        setMarcas([]);
        setMarcasSel([]);
        setMarcasGuardado([]);
        setMarcasFiltro("");
        setSubmarcas([]);
        setSubmarcasSel([]);
        setSubmarcasGuardado([]);
        setSubmarcasFiltro("");
      }

      setExpandedPanel(null);
      return;
    }

    if (panel === "marcas") {
      if (marcasAlteradas) {
        if (marcasSel.length === 0) {
          setSubmarcas([]);
          setSubmarcasSel([]);
          setSubmarcasGuardado([]);
          setSubmarcasFiltro("");
          setMarcasGuardado(marcasSel);
          setExpandedPanel(null);
          return;
        }

        setLoading(true);

        try {
          const dataini = dataIni;
          const datafim = dataFim;
          const refvendedor = podeVerTodosNoFabricanteSelecionado ? vendedorSel?.ref || "" : "";

          const submarcasAtuais = [...submarcasSel];

          await carregarSubmarcas({
            dataini,
            datafim,
            refvendedor,
            fabricantes: fabricantesSel,
            marcas: marcasSel,
            submarcasGuardadas: submarcasAtuais,
          });

          setMarcasGuardado(marcasSel);
        } finally {
          setLoading(false);
        }
      }

      setExpandedPanel(null);
      return;
    }

    if (panel === "submarcas") {
      if (submarcasAlteradas) {
        setSubmarcasGuardado(submarcasSel);
      }

      setExpandedPanel(null);
      return;
    }

    setExpandedPanel(null);
  };

  const toggleFabricante = async (ref) => {
    const novaSelecao = fabricantesSel[0] === ref ? [] : [ref];

    setFabricantesSel(novaSelecao);
    setFabricantesGuardado(novaSelecao);

    setMarcas([]);
    setMarcasSel([]);
    setMarcasGuardado([]);
    setMarcasFiltro("");

    setSubmarcas([]);
    setSubmarcasSel([]);
    setSubmarcasGuardado([]);
    setSubmarcasFiltro("");

    limparResultado();

    if (!dataIni || !dataFim) {
      return;
    }

    setLoading(true);

    try {
      const { vendedorDefault } = await carregarVendedores(
        dataIni,
        dataFim,
        null,
        novaSelecao[0] || ""
      );

      if (novaSelecao.length === 0) {
        return;
      }

      const refvendedor = podeVerTodosNoFabricanteSelecionado ? vendedorDefault?.ref || "" : "";

      const { selecionados: marcasDefault, submarcasGuardadas } = await carregarMarcas({
        dataini: dataIni,
        datafim: dataFim,
        refvendedor,
        fabricantes: novaSelecao,
      });

      if (marcasDefault.length === 0) {
        return;
      }

      await carregarSubmarcas({
        dataini: dataIni,
        datafim: dataFim,
        refvendedor,
        fabricantes: novaSelecao,
        marcas: marcasDefault,
        submarcasGuardadas,
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleMarca = (ref) => {
    setMarcasSel((prev) => {
      const novaLista = prev.includes(ref) ? prev.filter((x) => x !== ref) : [...prev, ref];

      setSubmarcas([]);
      setSubmarcasSel([]);
      setSubmarcasGuardado([]);
      setSubmarcasFiltro("");

      return novaLista;
    });
  };

  const toggleSubmarca = (ref) => {
    setSubmarcasSel((prev) =>
      prev.includes(ref) ? prev.filter((x) => x !== ref) : [...prev, ref]
    );
  };

  const toggleAllFabricantes = () => {
    if (fabricantesSel.length > 0) {
      setFabricantesSel([]);
      return;
    }

    const primeiroVisivel = fabricantesOpt[0]?.ref || "";
    setFabricantesSel(primeiroVisivel ? [primeiroVisivel] : []);
  };

  const toggleAllMarcas = () => {
    const visibleRefs = marcasOpt.map((x) => x.ref);

    const allVisibleSelected =
      visibleRefs.length > 0 && visibleRefs.every((ref) => marcasSel.includes(ref));

    if (allVisibleSelected) {
      setMarcasSel((prev) => prev.filter((ref) => !visibleRefs.includes(ref)));
    } else {
      setMarcasSel((prev) => Array.from(new Set([...prev, ...visibleRefs])));
    }
  };

  const toggleAllSubmarcas = () => {
    const visibleRefs = submarcasOpt.map((x) => x.ref);

    const allVisibleSelected =
      visibleRefs.length > 0 && visibleRefs.every((ref) => submarcasSel.includes(ref));

    if (allVisibleSelected) {
      setSubmarcasSel((prev) => prev.filter((ref) => !visibleRefs.includes(ref)));
    } else {
      setSubmarcasSel((prev) => Array.from(new Set([...prev, ...visibleRefs])));
    }
  };

  const limparResultado = () => {
    setResultado({
      submarcas: [],
      linhas: [],
    });
    setAnaliseCarregada(false);
  };

  const carregarPeriodoDefault = async () => {
    const [dados, err] = await handleApi(
      api.post("Comercial/SelPeriodoDefaultPropagacaoSubmarcas", {})
    );

    if (err) {
      showAlert(err, "error");
      return null;
    }

    return dados;
  };

  const carregarVendedores = async (
    datainiArg,
    datafimArg,
    vendedorPretendido = null,
    reffabricanteArg = ""
  ) => {
    const reffabricanteEfetivo = reffabricanteArg || fabricantesSel[0] || "";

    const body = {
      idutilizador: idUtilizador,
      funcoes: funcoesUser,
      dataini: datainiArg || dataIni,
      datafim: datafimArg || dataFim,
      reffabricante: reffabricanteEfetivo,
    };

    const [dados, err] = await handleApi(
      api.post("Comercial/SelVendedoresPropagacaoSubmarcasCombo", body)
    );

    if (err) {
      showAlert(err, "error");
      setVendedores([]);
      setVendedorSel(null);
      return { lista: [], vendedorDefault: null };
    }

    const lista = normalizeMulti(dados);

    const podeVerTodosVendedores =
      isAdminLike ||
      (isGestaoSuperBock && reffabricanteEfetivo === "1123110000") ||
      (isGestaoCocaCola && reffabricanteEfetivo === "1123110039");

    if (podeVerTodosVendedores) {
      const todos = { ref: "", nome: "Todos" };
      const final = [todos, ...lista];

      const vendedorFinal = final.find((x) => x.ref === (vendedorPretendido?.ref || "")) || todos;

      setVendedores(final);
      setVendedorSel(vendedorFinal);
      return { lista: final, vendedorDefault: vendedorFinal };
    }

    const vendedorFinal =
      lista.find((x) => x.ref === (vendedorPretendido?.ref || "")) || lista[0] || null;

    setVendedores(lista);
    setVendedorSel(vendedorFinal);
    return { lista, vendedorDefault: vendedorFinal };
  };

  const selFabricantes = async ({ dataini, datafim, refvendedor, term = "" }) => {
    const body = {
      idutilizador: idUtilizador,
      funcoes: funcoesUser,
      dataini,
      datafim,
      refvendedor,
      term,
    };

    const [dados, err] = await handleApi(
      api.post("Comercial/SelFabricantesPropagacaoSubmarcas", body)
    );

    if (err) {
      showAlert(err, "error");
      return [];
    }

    const lista = normalizeMulti(dados);

    console.log("=== SelFabricantesPropagacaoSubmarcas ===");
    console.log("Body enviado:", body);
    console.log("Dados recebidos:", dados);
    console.log("Lista normalizada:", lista);
    console.log("Quantidade:", lista.length);

    return lista;
  };

  const selMarcas = async ({ dataini, datafim, refvendedor, fabricantes, term = "" }) => {
    if (!Array.isArray(fabricantes) || fabricantes.length === 0) {
      return [];
    }

    const body = {
      idutilizador: idUtilizador,
      funcoes: funcoesUser,
      dataini,
      datafim,
      refvendedor,
      fabricantes,
      term,
    };

    const [dados, err] = await handleApi(api.post("Comercial/SelMarcasPropagacaoSubmarcas", body));

    if (err) {
      showAlert(err, "error");
      return [];
    }

    return normalizeMulti(dados);
  };

  const selSubmarcas = async ({
    dataini,
    datafim,
    refvendedor,
    fabricantes,
    marcas,
    term = "",
  }) => {
    if (!Array.isArray(fabricantes) || fabricantes.length === 0) {
      return [];
    }

    if (!Array.isArray(marcas) || marcas.length === 0) {
      return [];
    }

    const body = {
      idutilizador: idUtilizador,
      funcoes: funcoesUser,
      dataini,
      datafim,
      refvendedor,
      fabricantes,
      marcas,
      term,
    };

    const [dados, err] = await handleApi(
      api.post("Comercial/SelSubmarcasPropagacaoSubmarcas", body)
    );

    if (err) {
      showAlert(err, "error");
      return [];
    }

    return normalizeMulti(dados);
  };

  const selPropagacaoFabricante = async ({ reffabricante }) => {
    if (!reffabricante) {
      return { marcas: [], submarcas: [] };
    }

    const body = {
      idutilizador: idUtilizador,
      funcoes: funcoesUser,
      reffabricante,
    };

    const [dados, err] = await handleApi(api.post("Comercial/SelPropagacaoFabricante", body));

    if (err) {
      showAlert(err, "error");
      return { marcas: [], submarcas: [] };
    }

    return {
      marcas: Array.isArray(dados?.marcas)
        ? dados.marcas.map((x) => (x?.ref || "").toString())
        : [],
      submarcas: Array.isArray(dados?.submarcas)
        ? dados.submarcas.map((x) => (x?.ref || "").toString())
        : [],
    };
  };

  const gravarConfiguracaoFabricante = async () => {
    const reffabricante = fabricantesSel[0] || "";

    if (!reffabricante) {
      showAlert("Seleciona um fabricante.", "warning");
      setExpandedPanel("fabricantes");
      return;
    }

    if (!podeEditarFabricanteSelecionado) {
      showAlert("Não tem permissão para gravar a configuração deste fabricante.", "warning");
      return;
    }

    setLoading(true);

    try {
      const body = {
        idutilizador: idUtilizador,
        funcoes: funcoesUser,
        reffabricante,
        marcas: marcasSel,
        submarcas: submarcasSel,
      };

      const [dados, err, meta] = await handleApi(
        api.post("Comercial/UpdPropagacaoFabricante", body)
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      setFabricantesGuardado(fabricantesSel);
      setMarcasGuardado(marcasSel);
      setSubmarcasGuardado(submarcasSel);

      showAlert(meta?.message || "Configuração gravada com sucesso.", meta?.severity || "success");

      return dados;
    } finally {
      setLoading(false);
    }
  };

  const selMatriz = async ({
    dataini,
    datafim,
    refvendedor,
    modoclientes,
    fabricantes,
    marcas,
    submarcas,
  }) => {
    const body = {
      idutilizador: idUtilizador,
      funcoes: funcoesUser,
      dataini,
      datafim,
      refvendedor,
      modoclientes,
      fabricantes,
      marcas,
      submarcas,
    };

    const [dados, err] = await handleApi(api.post("Comercial/SelMatrizPropagacaoSubmarcas", body));

    if (err) {
      showAlert(err, "error");
      return null;
    }

    return {
      submarcas: Array.isArray(dados?.submarcas) ? dados.submarcas : [],
      linhas: Array.isArray(dados?.linhas) ? dados.linhas : [],
    };
  };

  const carregarFabricantes = async ({
    dataini = dataIni,
    datafim = dataFim,
    refvendedor = podeVerTodosNoFabricanteSelecionado ? vendedorSel?.ref || "" : "",
  } = {}) => {
    const lista = await selFabricantes({
      dataini,
      datafim,
      refvendedor,
      term: "",
    });

    if (!Array.isArray(lista) || lista.length === 0) {
      setFabricantes([]);
      setFabricantesSel([]);
      setFabricantesGuardado([]);
      setFabricantesFiltro("");
      setMarcas([]);
      setMarcasSel([]);
      setMarcasGuardado([]);
      setMarcasFiltro("");
      setSubmarcas([]);
      setSubmarcasSel([]);
      setSubmarcasGuardado([]);
      setSubmarcasFiltro("");
      return { lista: [], selecionados: [] };
    }

    let fabricanteGuardado = "";
    try {
      fabricanteGuardado = localStorage.getItem(FABRICANTE_STORAGE_KEY) || "";
    } catch {
      fabricanteGuardado = "";
    }

    let selecionados = [];

    if (fabricanteGuardado && lista.some((x) => x.ref === fabricanteGuardado)) {
      selecionados = [fabricanteGuardado];
    } else if (lista.some((x) => x.ref === "1123110000")) {
      selecionados = ["1123110000"];
    } else if (lista.length > 0) {
      selecionados = [lista[0].ref];
    }

    setFabricantes(lista);
    setFabricantesSel(selecionados);
    setFabricantesGuardado(selecionados);

    setFabricantesFiltro("");
    setMarcas([]);
    setMarcasSel([]);
    setMarcasGuardado([]);
    setMarcasFiltro("");
    setSubmarcas([]);
    setSubmarcasSel([]);
    setSubmarcasGuardado([]);
    setSubmarcasFiltro("");

    return { lista, selecionados };
  };

  const carregarMarcas = async ({
    dataini = dataIni,
    datafim = dataFim,
    refvendedor = podeVerTodosNoFabricanteSelecionado ? vendedorSel?.ref || "" : "",
    fabricantes = fabricantesSel,
  } = {}) => {
    if (!Array.isArray(fabricantes) || fabricantes.length === 0) {
      setMarcas([]);
      setMarcasSel([]);
      setMarcasGuardado([]);
      setMarcasFiltro("");
      setSubmarcas([]);
      setSubmarcasSel([]);
      setSubmarcasGuardado([]);
      setSubmarcasFiltro("");
      return { lista: [], selecionados: [] };
    }

    const lista = await selMarcas({
      dataini,
      datafim,
      refvendedor,
      fabricantes,
      term: "",
    });

    const reffabricante = fabricantes[0] || "";
    const config = await selPropagacaoFabricante({ reffabricante });

    const refsDisponiveis = Array.isArray(lista) ? lista.map((x) => x.ref) : [];

    const temConfigMarcas = Array.isArray(config.marcas) && config.marcas.length > 0;

    const selecionados = temConfigMarcas
      ? refsDisponiveis.filter((ref) => config.marcas.includes(ref))
      : refsDisponiveis;

    setMarcas(Array.isArray(lista) ? lista : []);
    setMarcasSel(selecionados);
    setMarcasGuardado(selecionados);
    setMarcasFiltro("");

    setSubmarcas([]);
    setSubmarcasSel([]);
    setSubmarcasGuardado([]);
    setSubmarcasFiltro("");

    return {
      lista: Array.isArray(lista) ? lista : [],
      selecionados,
      submarcasGuardadas: config.submarcas || [],
    };
  };

  const carregarSubmarcas = async ({
    dataini = dataIni,
    datafim = dataFim,
    refvendedor = podeVerTodosNoFabricanteSelecionado ? vendedorSel?.ref || "" : "",
    fabricantes = fabricantesSel,
    marcas = marcasSel,
    submarcasGuardadas = null,
  } = {}) => {
    if (
      !Array.isArray(fabricantes) ||
      fabricantes.length === 0 ||
      !Array.isArray(marcas) ||
      marcas.length === 0
    ) {
      setSubmarcas([]);
      setSubmarcasSel([]);
      setSubmarcasGuardado([]);
      setSubmarcasFiltro("");
      return { lista: [], selecionados: [] };
    }

    const lista = await selSubmarcas({
      dataini,
      datafim,
      refvendedor,
      fabricantes,
      marcas,
      term: "",
    });

    let refsGuardadas = Array.isArray(submarcasGuardadas) ? submarcasGuardadas : null;

    if (!refsGuardadas) {
      const reffabricante = fabricantes[0] || "";
      const config = await selPropagacaoFabricante({ reffabricante });
      refsGuardadas = config.submarcas || [];
    }

    const refsDisponiveis = Array.isArray(lista) ? lista.map((x) => x.ref) : [];

    const temConfigSubmarcas = Array.isArray(refsGuardadas) && refsGuardadas.length > 0;

    const selecionados = temConfigSubmarcas
      ? refsDisponiveis.filter((ref) => refsGuardadas.includes(ref))
      : refsDisponiveis;

    setSubmarcas(Array.isArray(lista) ? lista : []);
    setSubmarcasSel(selecionados);
    setSubmarcasGuardado(selecionados);
    setSubmarcasFiltro("");

    return { lista: Array.isArray(lista) ? lista : [], selecionados };
  };

  const atualizar = async ({
    dataini = dataIni,
    datafim = dataFim,
    refvendedor = podeVerTodosNoFabricanteSelecionado ? vendedorSel?.ref || "" : "",
    modoclientes = modoClientes?.value || "rota",
    fabricantes = fabricantesSel,
    marcas = marcasSel,
    submarcas = submarcasSel,
  } = {}) => {
    if (!dataini || !datafim) {
      showAlert("Indica o período.", "warning");
      return null;
    }

    if (!Array.isArray(fabricantes) || fabricantes.length === 0) {
      showAlert("Seleciona um fabricante.", "warning");
      setExpandedPanel("fabricantes");
      return null;
    }

    let marcasEfetivas = Array.isArray(marcas) ? marcas : [];
    let submarcasEfetivas = Array.isArray(submarcas) ? submarcas : [];

    if (
      !Array.isArray(marcasEfetivas) ||
      marcasEfetivas.length === 0 ||
      !Array.isArray(submarcasEfetivas) ||
      submarcasEfetivas.length === 0
    ) {
      const { lista: listaMarcas } = await carregarMarcas({
        dataini,
        datafim,
        refvendedor,
        fabricantes,
      });

      marcasEfetivas = Array.isArray(listaMarcas) ? listaMarcas.map((x) => x.ref) : [];

      const { lista: listaSubmarcas } = await carregarSubmarcas({
        dataini,
        datafim,
        refvendedor,
        fabricantes,
        marcas: marcasEfetivas,
      });

      submarcasEfetivas = Array.isArray(listaSubmarcas) ? listaSubmarcas.map((x) => x.ref) : [];
    }

    if (!Array.isArray(marcasEfetivas) || marcasEfetivas.length === 0) {
      showAlert("O fabricante selecionado não tem marcas disponíveis.", "warning");
      setExpandedPanel("fabricantes");
      return null;
    }

    if (!Array.isArray(submarcasEfetivas) || submarcasEfetivas.length === 0) {
      showAlert("O fabricante selecionado não tem submarcas disponíveis.", "warning");
      setExpandedPanel("fabricantes");
      return null;
    }

    setLoading(true);

    try {
      const dados = await selMatriz({
        dataini,
        datafim,
        refvendedor,
        modoclientes,
        fabricantes,
        marcas: marcasEfetivas,
        submarcas: submarcasEfetivas,
      });

      if (!dados) {
        limparResultado();
        return null;
      }

      setResultado(dados);
      setAnaliseCarregada(true);

      return dados;
    } finally {
      setLoading(false);
    }
  };

  const handleCarregarFiltros = async () => {
    const dados = await atualizar({
      dataini: dataIni,
      datafim: dataFim,
      refvendedor: podeVerTodosNoFabricanteSelecionado ? vendedorSel?.ref || "" : "",
      modoclientes: modoClientes?.value || "rota",
      fabricantes: fabricantesSel,
      marcas: marcasSel,
      submarcas: submarcasSel,
    });

    if (dados) {
      showAlert("Análise atualizada com sucesso.", "success");
    }
  };

  useEffect(() => {
    const refSelecionada = fabricantesSel[0] || "";

    if (!refSelecionada) {
      return;
    }

    try {
      localStorage.setItem(FABRICANTE_STORAGE_KEY, refSelecionada);
    } catch {}
  }, [fabricantesSel, FABRICANTE_STORAGE_KEY]);

  useEffect(() => {
    if (!dataIni || !dataFim) {
      return;
    }

    try {
      localStorage.setItem(DATAINI_STORAGE_KEY, dataIni);
      localStorage.setItem(DATAFIM_STORAGE_KEY, dataFim);
    } catch {}
  }, [dataIni, dataFim, DATAINI_STORAGE_KEY, DATAFIM_STORAGE_KEY]);

  useEffect(() => {
    const sincronizarFabricante = async () => {
      const reffabricante = fabricantesSel[0] || "";

      if (!reffabricante) {
        setMarcas([]);
        setMarcasSel([]);
        setMarcasGuardado([]);
        setMarcasFiltro("");
        setSubmarcas([]);
        setSubmarcasSel([]);
        setSubmarcasGuardado([]);
        setSubmarcasFiltro("");
        return;
      }

      setLoading(true);

      try {
        const refvendedor = podeVerTodosNoFabricanteSelecionado ? vendedorSel?.ref || "" : "";

        const { selecionados: marcasDefault, submarcasGuardadas } = await carregarMarcas({
          dataini: dataIni,
          datafim: dataFim,
          refvendedor,
          fabricantes: [reffabricante],
        });

        if (marcasDefault.length === 0) {
          setSubmarcas([]);
          setSubmarcasSel([]);
          setSubmarcasGuardado([]);
          setSubmarcasFiltro("");
          return;
        }

        await carregarSubmarcas({
          dataini: dataIni,
          datafim: dataFim,
          refvendedor,
          fabricantes: [reffabricante],
          marcas: marcasDefault,
          submarcasGuardadas,
        });

        setFabricantesGuardado([reffabricante]);
      } finally {
        setLoading(false);
      }
    };

    if (!dataIni || !dataFim) return;

    sincronizarFabricante();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricanteSelecionado, dataIni, dataFim, vendedorSel]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      try {
        let di = "";
        let df = "";

        try {
          di = localStorage.getItem(DATAINI_STORAGE_KEY) || "";
          df = localStorage.getItem(DATAFIM_STORAGE_KEY) || "";
        } catch {
          di = "";
          df = "";
        }

        if (!di || !df) {
          const periodo = await carregarPeriodoDefault();
          if (!periodo) return;

          di = periodo?.dataini || "";
          df = periodo?.datafim || "";
        }

        setDataIni(di);
        setDataFim(df);

        let vendedorDefault = null;
        let modoClientesDefault = modosClientesOptions[0];

        const { vendedorDefault: vendedorObtido } = await carregarVendedores(di, df, null, "");

        if (isAdminLike) {
          vendedorDefault = vendedorObtido || { ref: "", nome: "Todos" };
          modoClientesDefault = modosClientesOptions[1];
          setModoClientes(modosClientesOptions[1]);
        } else {
          vendedorDefault = vendedorObtido || null;
          modoClientesDefault = modosClientesOptions[0];
          setModoClientes(modosClientesOptions[0]);
        }

        const { selecionados: fabricantesDefault } = await carregarFabricantes({
          dataini: di,
          datafim: df,
          refvendedor: "",
        });

        let refvendedor = "";

        if (fabricantesDefault.length > 0) {
          const fabricanteDefault = fabricantesDefault[0] || "";

          const { vendedorDefault: vendedorRecalculado } = await carregarVendedores(
            di,
            df,
            null,
            fabricanteDefault
          );

          if (isAdminLike) {
            vendedorDefault = vendedorRecalculado || { ref: "", nome: "Todos" };
          } else {
            vendedorDefault = vendedorRecalculado || null;
          }

          const podeVerTodosNoFabricanteDefault =
            isAdminLike ||
            (isGestaoSuperBock && fabricanteDefault === "1123110000") ||
            (isGestaoCocaCola && fabricanteDefault === "1123110039");

          refvendedor = podeVerTodosNoFabricanteDefault ? vendedorDefault?.ref || "" : "";

          if ((vendedorRecalculado?.ref || "") === "") {
            setModoClientes(modosClientesOptions[1]);
          } else {
            setModoClientes(modosClientesOptions[0]);
          }
        }

        if (!podeGerirPropagacao) {
          setMarcas([]);
          setMarcasSel([]);
          setMarcasGuardado([]);
          setSubmarcas([]);
          setSubmarcasSel([]);
          setSubmarcasGuardado([]);
        }

        const { selecionados: marcasDefault, submarcasGuardadas } = await carregarMarcas({
          dataini: di,
          datafim: df,
          refvendedor,
          fabricantes: fabricantesDefault,
        });

        if (marcasDefault.length === 0) {
          setSubmarcas([]);
          setSubmarcasSel([]);
          limparResultado();
          setExpandedPanel("marcas");
          return;
        }

        const { selecionados: submarcasDefault } = await carregarSubmarcas({
          dataini: di,
          datafim: df,
          refvendedor,
          fabricantes: fabricantesDefault,
          marcas: marcasDefault,
          submarcasGuardadas,
        });

        if (submarcasDefault.length === 0) {
          limparResultado();
          setExpandedPanel("submarcas");
          return;
        }

        limparResultado();
        setExpandedPanel(null);
      } finally {
        setLoading(false);
      }
    };

    if (idUtilizador > 0) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idUtilizador, DATAINI_STORAGE_KEY, DATAFIM_STORAGE_KEY]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" flexDirection="column" gap={2}>
              <MDTypography variant="h6">Filtros</MDTypography>

              <Accordion
                expanded={expandedPanel === "fabricantes"}
                onChange={onAccordionChange("fabricantes")}
                disableGutters
                sx={{
                  backgroundColor: "#fff7f0",
                  borderRadius: 2,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    minHeight: 32,
                    px: 1.5,
                    "& .MuiAccordionSummary-content": {
                      margin: "4px 0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    },
                  }}
                >
                  <MDTypography variant="button" sx={{ fontSize: 12 }}>
                    Fabricantes
                  </MDTypography>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
                  <>
                    {/* ---------------- TOP (Super Bock / Coca-Cola) ---------------- */}
                    <MDBox mb={1}>
                      <MDBox display="flex" flexWrap="wrap" gap={1}>
                        {fabricantes
                          .filter(
                            (f) =>
                              f.ref === "1123110000" || // Super Bock
                              f.nome?.toLowerCase().includes("coca")
                          )
                          .map((f) => (
                            <MDBox key={f.ref} sx={{ minWidth: 260 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={fabricantesSel.includes(f.ref)}
                                    onChange={() => toggleFabricante(f.ref)}
                                  />
                                }
                                label={
                                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                                    {f.nome} <span style={{ color: "#9e9e9e" }}>({f.ref})</span>
                                  </span>
                                }
                              />
                            </MDBox>
                          ))}
                      </MDBox>
                    </MDBox>

                    {/* ---------------- FILTRO ---------------- */}
                    <MDBox mb={1}>
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        Filtrar fabricantes
                      </MDTypography>

                      <TextField
                        value={fabricantesFiltro}
                        onChange={(e) => setFabricantesFiltro(e.target.value)}
                        placeholder="Escreva para filtrar por nome ou ref..."
                        size="small"
                        fullWidth
                      />
                    </MDBox>

                    {/* ---------------- OUTROS FABRICANTES ---------------- */}
                    <MDBox mb={0.5}>
                      <MDTypography variant="button" sx={{ fontSize: 12 }}>
                        Outros fabricantes
                      </MDTypography>
                    </MDBox>

                    <MDBox mb={0.5}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={
                              fabricantesOpt.filter(
                                (f) =>
                                  f.ref !== "1123110000" && !f.nome?.toLowerCase().includes("coca")
                              ).length > 0 &&
                              fabricantesOpt
                                .filter(
                                  (f) =>
                                    f.ref !== "1123110000" &&
                                    !f.nome?.toLowerCase().includes("coca")
                                )
                                .every((f) => fabricantesSel.includes(f.ref))
                            }
                            onChange={() => {
                              const outros = fabricantesOpt
                                .filter(
                                  (f) =>
                                    f.ref !== "1123110000" &&
                                    !f.nome?.toLowerCase().includes("coca")
                                )
                                .map((f) => f.ref);

                              const todosSelecionados = outros.every((r) =>
                                fabricantesSel.includes(r)
                              );

                              if (todosSelecionados) {
                                setFabricantesSel([]);
                              } else {
                                setFabricantesSel(outros[0] ? [outros[0]] : []);
                              }
                            }}
                          />
                        }
                        label={
                          <span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>
                        }
                      />
                    </MDBox>

                    <MDBox
                      sx={{
                        maxHeight: 220,
                        overflowY: "auto",
                        pr: 1,
                      }}
                    >
                      <MDBox display="flex" flexWrap="wrap" gap={1}>
                        {fabricantesOpt
                          .filter(
                            (f) => f.ref !== "1123110000" && !f.nome?.toLowerCase().includes("coca")
                          )
                          .map((f) => (
                            <MDBox key={f.ref} sx={{ minWidth: 260 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={fabricantesSel.includes(f.ref)}
                                    onChange={() => toggleFabricante(f.ref)}
                                  />
                                }
                                label={
                                  <span style={{ fontSize: 13 }}>
                                    {f.nome} <span style={{ color: "#9e9e9e" }}>({f.ref})</span>
                                  </span>
                                }
                              />
                            </MDBox>
                          ))}

                        {fabricantesOpt.filter(
                          (f) => f.ref !== "1123110000" && !f.nome?.toLowerCase().includes("coca")
                        ).length === 0 && (
                          <MDTypography variant="caption" color="text">
                            Sem resultados para os filtros indicados.
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>
                  </>
                </AccordionDetails>
              </Accordion>

              {fabricantesSel.length > 0 && (
                <Accordion
                  expanded={expandedPanel === "marcas"}
                  onChange={onAccordionChange("marcas")}
                  disableGutters
                  disabled={fabricantesSel.length === 0}
                  sx={{
                    backgroundColor: "#fff7f0",
                    borderRadius: 2,
                    "&:before": { display: "none" },
                    opacity: fabricantesSel.length === 0 ? 0.65 : 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      minHeight: 32,
                      px: 1.5,
                      "& .MuiAccordionSummary-content": {
                        margin: "4px 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      },
                    }}
                  >
                    <MDTypography variant="button" sx={{ fontSize: 12 }}>
                      Marcas
                    </MDTypography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
                    <ChecklistGroup
                      title="Marcas"
                      filterValue={marcasFiltro}
                      onFilterChange={setMarcasFiltro}
                      options={marcasOpt}
                      selectedRefs={marcasSel}
                      onToggle={toggleMarca}
                      onToggleAll={toggleAllMarcas}
                    />
                  </AccordionDetails>
                </Accordion>
              )}

              {fabricantesSel.length > 0 && (
                <Accordion
                  expanded={expandedPanel === "submarcas"}
                  onChange={onAccordionChange("submarcas")}
                  disableGutters
                  disabled={fabricantesSel.length === 0 || marcasSel.length === 0}
                  sx={{
                    backgroundColor: "#fff7f0",
                    borderRadius: 2,
                    "&:before": { display: "none" },
                    opacity: fabricantesSel.length === 0 || marcasSel.length === 0 ? 0.65 : 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      minHeight: 32,
                      px: 1.5,
                      "& .MuiAccordionSummary-content": {
                        margin: "4px 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      },
                    }}
                  >
                    <MDTypography variant="button" sx={{ fontSize: 12 }}>
                      Submarcas
                    </MDTypography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
                    <ChecklistGroup
                      title="Submarcas"
                      filterValue={submarcasFiltro}
                      onFilterChange={setSubmarcasFiltro}
                      options={submarcasOpt}
                      selectedRefs={submarcasSel}
                      onToggle={toggleSubmarca}
                      onToggleAll={toggleAllSubmarcas}
                    />
                  </AccordionDetails>
                </Accordion>
              )}

              <MDBox
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  md: isAdminLike || isGestaoFabricante ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr",
                  lg: isAdminLike || isGestaoFabricante ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr",
                }}
                gap={2}
              >
                <MDBox>
                  <MDTypography variant="button" color="text" fontWeight="regular" mb={0.5}>
                    Data inicial
                  </MDTypography>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={dataIni}
                    onChange={(e) => setDataIni(e.target.value)}
                  />
                </MDBox>

                <MDBox>
                  <MDTypography variant="button" color="text" fontWeight="regular" mb={0.5}>
                    Data final
                  </MDTypography>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                  />
                </MDBox>

                {(isAdminLike || isGestaoFabricante) && (
                  <MDBox>
                    <MDTypography variant="button" color="text" fontWeight="regular" mb={0.5}>
                      Vendedor
                    </MDTypography>
                    <Autocomplete
                      size="small"
                      options={vendedores}
                      getOptionLabel={(option) => option?.nome || ""}
                      value={vendedorSel}
                      onChange={(_, value) => {
                        const novo = value || vendedores[0] || null;
                        setVendedorSel(novo);

                        if (podeVerTodosNoFabricanteSelecionado && (novo?.ref || "") === "") {
                          setModoClientes(modosClientesOptions[1]);
                        } else {
                          setModoClientes(modosClientesOptions[0]);
                        }
                      }}
                      isOptionEqualToValue={(option, value) => option?.ref === value?.ref}
                      renderInput={(params) => <TextField {...params} placeholder="Selecione…" />}
                      disableClearable
                    />
                  </MDBox>
                )}

                <MDBox>
                  <MDTypography variant="button" color="text" fontWeight="regular" mb={0.5}>
                    Clientes a considerar
                  </MDTypography>
                  <Autocomplete
                    size="small"
                    options={modosClientesOptions}
                    getOptionLabel={(option) => option?.label || ""}
                    value={modoClientes}
                    onChange={(_, value) => setModoClientes(value || modosClientesOptions[0])}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => <TextField {...params} placeholder="Selecione…" />}
                    disableClearable
                  />
                </MDBox>
              </MDBox>

              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton color="success" onClick={handleCarregarFiltros}>
                  Atualizar Análise
                </MDButton>

                {podeGerirPropagacao && podeEditarFabricanteSelecionado && (
                  <MDButton
                    color="info"
                    onClick={gravarConfiguracaoFabricante}
                    disabled={fabricantesSel.length === 0}
                  >
                    Gravar Submarcas do Fabricante
                  </MDButton>
                )}
              </MDBox>

              {loading && (
                <MDBox display="flex" justifyContent="center" alignItems="center" py={1}>
                  <CircularProgress />
                </MDBox>
              )}

              {analiseCarregada && (
                <ResultadosMatriz
                  resultado={resultado}
                  mostrarColunaVendedor={mostrarColunaVendedor}
                />
              )}
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
